import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Sheet, SheetHeader, SheetTitle } from "@repo/blacksmith-ui/sheet";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cn } from "@repo/blacksmith-ui/cn";
import { formatTestDuration } from "@/lib/utils";
import { buildTestUrl } from "@/lib/test-url-utils";
import { X, Check, AlertCircle, ArrowRight } from "lucide-react";
import { TestLogViewer } from "./TestLogViewer";
import { Button } from "@repo/blacksmith-ui/button";
import { TestHistoryRun } from "@/hooks/useTestHistory";
import { formatDistanceToNow } from "date-fns";
import { GitCommit, Calendar, GitPullRequest } from "lucide-react";

// Status badge component for displaying test status with appropriate styling
const StatusBadge = t0 => {
  const $ = _c(4);
  const {
    status
  } = t0;
  switch (status) {
    case "pass":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400 border border-green-200 dark:border-green-900"><Check className="h-3 w-3" />PASSED</span>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "fail":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400 border border-red-200 dark:border-red-900"><X className="h-3 w-3" />FAILED</span>;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "error":
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-400 border border-orange-200 dark:border-orange-900"><AlertCircle className="h-3 w-3" />ERROR</span>;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case "skip":
      {
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-900"><ArrowRight className="h-3 w-3" />SKIPPED</span>;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
  }
};
import { useDashboardAppRouter as useRouter } from "@/hooks/useDashboardRouter";
import Link from "next/link";

// Helper function to parse timestamp as UTC
// API returns timestamps like "2025-12-18 22:45:05" in UTC
const parseUTCTimestamp = (ts: string): Date => {
  // If already has timezone indicator, parse as-is
  if (ts.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(ts)) {
    return new Date(ts);
  }
  // Replace space with 'T' for ISO format and append 'Z' for UTC
  const isoFormat = ts.replace(" ", "T") + "Z";
  return new Date(isoFormat);
};

// Custom SheetContent without overlay and without blocking pointer events
const SheetContentNoOverlay = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>>((t0, ref) => {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("fixed inset-y-0 right-0 z-50 h-full w-3/4 gap-4 border-l bg-app-background p-6 shadow-lg sm:max-w-2xl transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-125 data-[state=open]:duration-125 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SheetPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus-visible:outline-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 disabled:pointer-events-none data-[state=open]:bg-neutral-100 dark:ring-offset-neutral-950 dark:data-[state=open]:bg-neutral-900"><X className="h-4 w-4" /><span className="sr-only">Close</span></SheetPrimitive.Close>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== ref || $[10] !== t1) {
    t3 = <SheetPrimitive.Portal><SheetPrimitive.Content ref={ref} className={t1} {...props}>{children}{t2}</SheetPrimitive.Content></SheetPrimitive.Portal>;
    $[7] = children;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
});
SheetContentNoOverlay.displayName = "SheetContentNoOverlay";
interface TestFailureDrawerProps {
  run: TestHistoryRun | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  org: string;
  repository: string;
  testName?: string;
  jobId?: string; // Current job ID - empty/undefined means workflow level
}

// Helper component for displaying a field in compact grid format
const InfoField = t0 => {
  const $ = _c(12);
  const {
    label,
    value,
    link,
    externalLink,
    org
  } = t0;
  if (!value) {
    return null;
  }
  let t1;
  if ($[0] !== externalLink || $[1] !== link || $[2] !== org || $[3] !== value) {
    t1 = link ? externalLink ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-left text-neutral-900 dark:text-neutral-100 hover:underline">{value}</a> : <Link href={`/${org}/${link}`} className="underline hover:text-neutral-700 dark:hover:text-neutral-300">{value}</Link> : <span className="text-neutral-900 dark:text-neutral-100">{value}</span>;
    $[0] = externalLink;
    $[1] = link;
    $[2] = org;
    $[3] = value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const content = t1;
  let t2;
  if ($[5] !== label) {
    t2 = <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 whitespace-nowrap">{label}</span>;
    $[5] = label;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== content) {
    t3 = <div className="text-xs text-right flex-1 min-w-0">{content}</div>;
    $[7] = content;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex items-start justify-between gap-4">{t2}{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
export const TestFailureDrawer = React.memo<TestFailureDrawerProps>(t0 => {
  const $ = _c(27);
  const {
    run,
    isOpen,
    onOpenChange,
    org,
    repository,
    testName,
    jobId
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] !== run) {
    t1 = run?.timestamp ? formatDistanceToNow(parseUTCTimestamp(run.timestamp), {
      addSuffix: true
    }) : "Unknown";
    $[0] = run;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timestamp = t1;
  let t2;
  if ($[2] !== run) {
    t2 = run?.sha ? run.sha.substring(0, 7) : "\u2014";
    $[2] = run;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shortSha = t2;
  const t3 = run?.duration_seconds;
  let t4;
  if ($[4] !== t3) {
    t4 = formatTestDuration(t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const durationText = t4;
  let t5;
  if ($[6] !== jobId || $[7] !== org || $[8] !== run) {
    t5 = run?.run_id ? buildTestUrl({
      org,
      runId: run.run_id,
      testId: run.test_id,
      jobId,
      targetJobId: run.job_id
    }) : null;
    $[6] = jobId;
    $[7] = org;
    $[8] = run;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const testUrl = t5;
  let t6;
  if ($[10] !== durationText || $[11] !== onOpenChange || $[12] !== org || $[13] !== repository || $[14] !== router || $[15] !== run || $[16] !== shortSha || $[17] !== testName || $[18] !== testUrl || $[19] !== timestamp) {
    t6 = !run ? <div className="p-6">Loading...</div> : <div className="flex flex-col h-full"><SheetHeader className="border-b pb-4 flex-shrink-0"><SheetTitle className="type-page-title-l">{testName && run?.sha ? `${testName} (${run.sha.substring(0, 7)})` : testName ? testName : "Test Failure Details"}</SheetTitle></SheetHeader><div className="py-6 flex flex-col flex-1 min-h-0 gap-6"><div className="border rounded-lg bg-muted/30 p-4 space-y-3 flex-shrink-0"><div className="flex items-center justify-between pb-2 border-b"><h3 className="type-page-title-m">Test run information</h3><div className="shrink-0"><StatusBadge status={run.status} /></div></div><div className="space-y-2"><InfoField label="Timestamp" value={timestamp} org={org} />{run.sha && <InfoField label="Commit" value={run.commit_message ? `${run.commit_message.split("\n")[0]} (${shortSha})` : shortSha} link={`https://github.com/${repository}/commit/${run.sha}`} externalLink={true} org={org} />}<InfoField label="Duration" value={durationText} org={org} />{run.job_id && run.run_id && <InfoField label="Job ID" value={run.job_id} link={`runs/${run.run_id}/jobs/${run.job_id}`} org={org} />}{run.pr_number && <InfoField label="Pull Request" value={`PR #${run.pr_number}`} link={`https://github.com/${repository}/pull/${run.pr_number}`} externalLink={true} org={org} />}{run.branch && <InfoField label="Branch" value={run.branch} org={org} />}<InfoField label="Repository" value={repository} link={`https://github.com/${repository}`} externalLink={true} org={org} /></div></div>{run?.logs && <div className="flex flex-col gap-3 flex-1 min-h-0"><h3 className="type-page-title-m flex-shrink-0">{run.status === "fail" || run.status === "error" ? "Error" : "Logs"}</h3><TestLogViewer logs={run.logs} mode="clamp" className="flex-1" /></div>}{testUrl && <div className="flex justify-end flex-shrink-0"><Button variant="secondary" onClick={e => {
            onOpenChange(false);
            if (e.metaKey || e.ctrlKey) {
              window.open(testUrl, "_blank");
            } else {
              router.push(testUrl);
            }
          }}>View details</Button></div>}</div></div>;
    $[10] = durationText;
    $[11] = onOpenChange;
    $[12] = org;
    $[13] = repository;
    $[14] = router;
    $[15] = run;
    $[16] = shortSha;
    $[17] = testName;
    $[18] = testUrl;
    $[19] = timestamp;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const content = t6;
  let t7;
  if ($[21] !== content) {
    t7 = <SheetContentNoOverlay className="w-full overflow-hidden flex flex-col">{content}</SheetContentNoOverlay>;
    $[21] = content;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== isOpen || $[24] !== onOpenChange || $[25] !== t7) {
    t8 = <Sheet open={isOpen} onOpenChange={onOpenChange} modal={false}>{t7}</Sheet>;
    $[23] = isOpen;
    $[24] = onOpenChange;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
});
TestFailureDrawer.displayName = "TestFailureDrawer";